import { forwardRef } from 'react';
import clsx from 'clsx';

import type { FieldConfidenceResponse } from '../api';
import type { FieldEditState } from '../hooks/useFieldEdit';
import { ConfidenceMeter } from './ConfidenceMeter';
import { EditableValue } from './EditableValue';
import { ManualCorrectionDiff } from './ManualCorrectionDiff';
import { TableValueView } from './TableValueView';

interface FieldRowProps {
  field: FieldConfidenceResponse;
  edit: FieldEditState | undefined;
  missingRequired: boolean;
  highlighted: boolean;
  onCommit: (fieldName: string, value: unknown) => void;
  onFocus: (fieldName: string) => void;
}

/**
 * One row of the right-hand reviewer panel.
 *
 * Responsibilities:
 *  - Render the field name, score badge (or pencil if manually edited), value,
 *    and any validation issue / expected hint.
 *  - Bubble focus events to the parent so the PDF overlay can scroll to the
 *    matching bounding box.
 */
export const FieldRow = forwardRef<HTMLDivElement, FieldRowProps>(function FieldRow(
  { field, edit, missingRequired, highlighted, onCommit, onFocus },
  ref,
): JSX.Element {
  const effectiveValue = edit !== undefined ? edit.value : field.value;
  const manuallyEdited = edit?.manuallyEdited ?? false;
  const inputId = `field-${field.name}`;

  const isSaving = edit?.saving === true;

  return (
    <div
      ref={ref}
      role="group"
      aria-labelledby={`${inputId}-label`}
      onFocus={() => onFocus(field.name)}
      onClick={() => onFocus(field.name)}
      className={clsx(
        'group relative rounded-xl border bg-white p-3 shadow-sm transition-all duration-200 ease-out',
        'hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md',
        highlighted
          ? 'border-primary/60 shadow-lg ring-2 ring-primary/30'
          : 'border-slate-200',
        manuallyEdited && 'animate-flash-glow',
      )}
    >
      <div className="flex items-start gap-3">
        <ConfidenceMeter
          score={field.score}
          status={field.status}
          manuallyEdited={manuallyEdited}
        />
        <div className="min-w-0 flex-1">
          <label
            id={`${inputId}-label`}
            htmlFor={inputId}
            className="block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            {field.name}
          </label>
          <div className="mt-1">
            {isTableValue(effectiveValue) ? (
              <TableValueView rows={effectiveValue} />
            ) : (
              <EditableValue
                inputId={inputId}
                value={effectiveValue}
                missing={missingRequired}
                disabled={isSaving}
                onCommit={(next) => onCommit(field.name, next)}
              />
            )}
          </div>
          {manuallyEdited && !isTableValue(effectiveValue) ? (
            <ManualCorrectionDiff originalValue={field.value} correctedValue={effectiveValue} />
          ) : null}
          {field.issue != null && field.issue.length > 0 ? (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 h-3.5 w-3.5 flex-none">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
              <span>
                <span className="font-semibold">{field.issue}</span>
                {field.expectedHint != null && field.expectedHint.length > 0 ? (
                  <span className="opacity-80"> — {field.expectedHint}</span>
                ) : null}
              </span>
            </p>
          ) : null}
        </div>
      </div>
      {isSaving ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl"
        >
          <div className="animate-shimmer h-full w-full bg-[linear-gradient(110deg,transparent_0%,rgba(79,70,229,0.06)_30%,rgba(79,70,229,0.12)_50%,rgba(79,70,229,0.06)_70%,transparent_100%)] bg-[length:200%_100%]" />
        </div>
      ) : null}
    </div>
  );
});

/**
 * Type guard: a Table-typed extracted value arrives as an array of plain objects.
 * Tolerates the loose typing of FieldConfidenceResponse.value (unknown) by checking
 * the runtime shape rather than relying on a schema reference (which isn't carried
 * in the reviewer's data path yet).
 */
function isTableValue(value: unknown): value is ReadonlyArray<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every(
    (row) => typeof row === 'object' && row !== null && !Array.isArray(row),
  );
}
